import { useState, useEffect, useCallback, useRef } from 'react';
import { notesAPI } from '@/lib/api';
import * as offlineDB from '@/lib/offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Cross-device note freshness via SSE + focus/resume revalidation.
 * 
 * When a note is saved on another device/tab:
 * - Clean open note → auto-refresh silently
 * - Dirty open note → show conflict banner
 * - Notes in list → auto-refresh in background
 * - Origin session → skip (already fresh)
 */
export function useLiveSync({
  notes, setNotes, decryptNote, selectedNoteId, isSavedRef, sessionIdRef,
}) {
  const [staleNoteEvent, setStaleNoteEvent] = useState(null);
  const lastSyncTimeRef = useRef(new Date().toISOString());
  const eventSourceRef = useRef(null);
  const notesRef = useRef(notes);
  const selectedNoteIdRef = useRef(selectedNoteId);
  const revalidationTimerRef = useRef(null);
  const sseErrorCountRef = useRef(0);
  const batchQueueRef = useRef([]);
  const batchTimerRef = useRef(null);

  // Keep refs current
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { selectedNoteIdRef.current = selectedNoteId; }, [selectedNoteId]);

  // Refresh a single note from server
  const refreshNote = useCallback(async (noteId) => {
    try {
      const res = await notesAPI.get(noteId);
      const fresh = await decryptNote(res.data);
      setNotes(prev => prev.map(n => n.id === noteId ? fresh : n));
      await offlineDB.saveNote(fresh);
    } catch (err) {
      console.warn('[LiveSync] Failed to refresh note:', noteId, err);
    }
  }, [decryptNote, setNotes]);

  // Ref-based handler for SSE events (stable across re-renders)
  const handleSyncEventRef = useRef(null);
  handleSyncEventRef.current = (event) => {
    if (event.type !== 'note_updated') return;
    if (event.origin_session_id === sessionIdRef.current) return;

    const { note_id } = event;
    const isCurrentNote = selectedNoteIdRef.current === note_id;

    if (isCurrentNote && isSavedRef.current === false) {
      setStaleNoteEvent(event);
    } else {
      // Batch refreshes: coalesce rapid updates within 200ms
      batchQueueRef.current.push(note_id);
      clearTimeout(batchTimerRef.current);
      batchTimerRef.current = setTimeout(async () => {
        const noteIds = [...new Set(batchQueueRef.current)];
        batchQueueRef.current = [];
        for (const id of noteIds) {
          await refreshNote(id);
        }
      }, 200);
    }
  };

  // SSE connection — stable, opened once (item 2 + 7)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `${API_URL}/api/sync/events?token=${token}`;
    const es = new EventSource(url);

    es.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        if (event.type === 'connected') {
          sseErrorCountRef.current = 0;
          console.log('[LiveSync] SSE connected');
          return;
        }
        handleSyncEventRef.current(event);
        lastSyncTimeRef.current = new Date().toISOString();
      } catch (err) {
        console.warn('[LiveSync] Failed to parse SSE event:', err);
      }
    };

    es.onerror = () => {
      // Rate-limit reconnection logging (item 7)
      sseErrorCountRef.current++;
      if (sseErrorCountRef.current <= 3 || sseErrorCountRef.current % 50 === 0) {
        console.log(`[LiveSync] SSE reconnecting (attempt ${sseErrorCountRef.current})`);
      }
    };

    eventSourceRef.current = es;
    return () => {
      es.close();
      eventSourceRef.current = null;
      clearTimeout(batchTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus/resume revalidation with dedup guard (item 4)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const revalidate = async () => {
      if (!navigator.onLine) return;

      try {
        const since = lastSyncTimeRef.current;
        const res = await fetch(`${API_URL}/api/sync/changes?since=${encodeURIComponent(since)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        lastSyncTimeRef.current = data.server_time;

        if (!data.changes || data.changes.length === 0) return;

        for (const change of data.changes) {
          const localNote = notesRef.current.find(n => n.id === change.id);
          if (!localNote) continue;

          const localRev = localNote.revision || 0;
          const serverRev = change.revision || 0;
          if (serverRev <= localRev) continue;

          const isCurrentNote = selectedNoteIdRef.current === change.id;
          if (isCurrentNote && isSavedRef.current === false) {
            setStaleNoteEvent({
              type: 'note_updated',
              note_id: change.id,
              revision: serverRev,
              updated_at: change.updated_at,
              origin_session_id: null,
            });
          } else {
            await refreshNote(change.id);
          }
        }
      } catch (err) {
        console.warn('[LiveSync] Focus revalidation failed:', err);
      }
    };

    // Debounced handler — deduplicates visibilitychange + focus firing together
    const handleFocusResume = () => {
      if (document.visibilityState === 'hidden') return;
      clearTimeout(revalidationTimerRef.current);
      revalidationTimerRef.current = setTimeout(revalidate, 400);
    };

    document.addEventListener('visibilitychange', handleFocusResume);
    window.addEventListener('focus', handleFocusResume);
    return () => {
      document.removeEventListener('visibilitychange', handleFocusResume);
      window.removeEventListener('focus', handleFocusResume);
      clearTimeout(revalidationTimerRef.current);
    };
  }, [refreshNote, isSavedRef]);

  // Dismiss conflict banner
  const dismissStaleNote = useCallback(() => {
    setStaleNoteEvent(null);
  }, []);

  // Accept server version (reload)
  const acceptServerVersion = useCallback(async () => {
    if (!staleNoteEvent) return;
    await refreshNote(staleNoteEvent.note_id);
    setStaleNoteEvent(null);
  }, [staleNoteEvent, refreshNote]);

  return {
    staleNoteEvent,
    dismissStaleNote,
    acceptServerVersion,
  };
}
